<script lang="ts">
    import Select from "svelte-select/Select.svelte"

    import { getCachedEntries } from "../../../api"
    import { createEventDispatcher } from "svelte"

    type Value = string | number | string[] | number[]
    type ValueStorage =
        | {
              value: string | number
              label: string
          }
        | {
              value: string | number
              label: string
          }[]
    export let field: TaskStepField<"select">,
        value: Value = field.widget.includes("[]") ? [] : "",
        editMode = true

    const dispatch = createEventDispatcher()
    if (field.widget.includes("[]") && !Array.isArray(value)) value = []
    const endpointCall = async (options: EndpointOptions) => {
        try {
            const response = await getCachedEntries(options.endpoint, {
                ...options.params.filter,
            })
            const valueStorage: ValueStorage = []

            response.forEach((item) => {
                if (options.mapping) {
                    valueStorage.push({
                        value: item[options.mapping["id"]] || "",
                        label: item[options.mapping["name"]] || "",
                    })
                }
            })
            return valueStorage
        } catch (e) {
            throw e
        }
    }
    // Set initial default value for multiple select
    let multiple = field.widget?.endsWith("[]"),
        valueStorage: ValueStorage = multiple ? [] : { value: null, label: null },
        options: ValueStorage = [],
        loaded = false

    $: if (multiple && !value) value = []
    if (Array.isArray(field?.options)) {
        options = field.options
        if (multiple) valueStorage = options.filter((choice) => value.includes(choice.value))
        else valueStorage = options.find((choice) => choice.value === value)
        loaded = true
    } else if (typeof field?.options === "object") {
        // @ts-ignore

        endpointCall({ ...field.options, endpoint: `${field.options.endpoint}` })
            .then((optionsRes) => {
                options = optionsRes
                if (multiple) {
                    valueStorage = options.filter((choice) => value.includes(choice.value))
                } else {
                    valueStorage = options.find((choice) => choice.value === value)
                }
            })
            .catch(console.error)
            .finally(() => {
                loaded = true
            })
    } else loaded = true
    $: if (loaded && !!valueStorage) {
        const newValue = Array.isArray(valueStorage) ? valueStorage.map((item) => item.value) : valueStorage?.value
        if (JSON.stringify(newValue) !== JSON.stringify(value)) {
            value = newValue
            dispatch("valueChanged")
        }
    }
    $: hasValue = multiple ? value.length > 0 : !!value
    let listOpen = false
</script>

<!-- svelte-ignore a11y-label-has-associated-control -->
<label
    class="svelte-select-label {multiple ? 'multi-input' : ''}"
    class:noEditMode="{!editMode}"
>
    {#if editMode}
        <div
            class="selectWrapper"
            class:hasAValue="{hasValue}"
        >
            {#if hasValue}
                <p class:hasValue="{hasValue}">
                    {field.label}
                </p>
            {/if}
            {#key options}
                <Select
                    items="{options}"
                    inputAttributes="{{ autocomplete: 'on' }}"
                    showChevron="{false}"
                    clearable="{false}"
                    hideEmptyState="{true}"
                    searchable="{true}"
                    multiple="{multiple}"
                    bind:value="{valueStorage}"
                    placeholder="{field.label}"
                    on:clear="{(e) => (valueStorage = valueStorage)}"
                    bind:listOpen="{listOpen}"
                />
            {/key}
        </div>
        <button
            class="chevron"
            on:click|preventDefault|stopPropagation="{() => (listOpen = !listOpen)}"
        >
            <img
                src="../../../assets/icons/solar_alt-arrow-down-outline.svg"
                alt="icon"
            /></button
        >
    {:else}
        <div class="no-input">
            {#if valueStorage && hasValue}
                {#if multiple}
                    {#each valueStorage as item}
                        <div class="select-no-input">{item.label}</div>
                    {/each}
                {:else}
                    <div class="select-no-input">{valueStorage.label}</div>
                {/if}
            {/if}
        </div>
    {/if}
</label>

<style
    lang="less"
    global
>
    .noEditMode {
        min-height: 54px !important;
        max-height: 54px;
        display: flex;
        align-items: center;
        .no-input {
            display: flex;
            align-items: center;
        }
    }
    .svelte-select-label {
        border: 1px solid var(--grey-200) !important;
        background-color: var(--white-100);
        min-height: 100%;
        max-height: 54px;

        display: flex;
        flex-direction: row !important;
        align-items: center;

        .chevron {
            height: 1.2rem;
            width: 1.2rem;
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
        }
        .svelte-select {
            max-width: 90%;
        }
        .multi-item-text {
            display: flex;
            align-items: center;
        }
        padding: 0.3rem !important;
        p {
            opacity: 0;
            transition: opacity 0.4s;
        }
        p.hasValue {
            opacity: 1;
        }
        .selectWrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex-grow: 1;
            width: 100%;
            &.hasAValue {
                padding-top: 0.5rem;
            }
        }
        .svelte-select,
        .no-input {
            border: 0px solid black !important;
            cursor: pointer;
            max-height: 30px !important;
            min-height: 30px !important;

            .svelte-select-list .item {
                color: black !important;
            }
            * {
                color: var(--text-100) !important;
                max-height: 30px;
            }
            .svelte-select-list {
                * {
                    max-height: unset !important;
                }
            }
        }

        .value-container {
            flex-wrap: wrap;
            max-width: 80%;
            overflow: auto !important;
        }
        &.multi-input {
            max-height: unset !important;
            min-height: unset;
            .svelte-select,
            .no-input {
                max-height: unset !important;
            }
            .value-container {
                min-height: fit-content !important;
                max-height: unset !important;
            }
        }
        &.noEditMode {
            justify-content: flex-start;
            align-items: center;
            .no-input {
                height: 100%;
                display: flex;
                flex-wrap: wrap;
                gap: 0.6rem;
                align-items: center;
                .select-no-input {
                    font-family: Inter !important;
                    font-size: 0.8rem !important;

                    font-weight: 400;
                    color: var(--text-100) !important;
                }
            }
        }
    }
</style>
